import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FolderOpen, FileVideo, Pencil, CalendarClock, Radio, MoreVertical,
  Loader2, Upload, ArrowLeft, Image as ImageIcon, Images, Link2, Scissors,
  Sparkles, LayoutTemplate,
} from 'lucide-react';
import { toast } from 'sonner';
import ProjectFormDemo, { ProductionQueueDemo } from '@/components/project-templates/ProjectFormDemo';
import { LinkedTemplateFoldersPanel } from '@/components/templates/LinkedTemplateFoldersPanel';

const API_URL = window.__BACKEND_URL;
const OVERRIDES_PREFIX = 'consistency_demo_project_overrides_';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'];
const isImageAsset = (asset) => {
  const key = (asset.input_file_url || '').toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => key.endsWith(ext));
};

function loadOverrides(creatorId) {
  try { return JSON.parse(localStorage.getItem(`${OVERRIDES_PREFIX}${creatorId}`)) || {}; }
  catch { return {}; }
}
function saveOverrides(creatorId, overrides) {
  localStorage.setItem(`${OVERRIDES_PREFIX}${creatorId}`, JSON.stringify(overrides));
}

export default function ProjectOverview() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [parentAssets, setParentAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [thumbMap, setThumbMap] = useState({});
  const [thumbUploadingId, setThumbUploadingId] = useState(null);
  const thumbInputRef = React.useRef(null);
  const thumbTargetRef = React.useRef(null);

  const [nameDraft, setNameDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeToolKey, setActiveToolKey] = useState(null);
  const autoToolFiredRef = useRef(false);

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' });
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          throw new Error('Could not load this project');
        }
        const data = await res.json();
        const overrides = loadOverrides(data.project.creator_id);
        const overriddenName = overrides[data.project.id]?.name || data.project.name;
        setProject({ ...data.project, name: overriddenName });
        setNameDraft(overriddenName);
        setParentAssets(data.parent_assets || []);
      } catch (err) {
        toast.error(err.message || 'Could not load this project');
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    const loadThumbs = async () => {
      const results = await Promise.all(parentAssets.map(async (a) => {
        try {
          const res = await fetch(`${API_URL}/api/admin/content-sources/${a.id}/thumbnail`, { credentials: 'include' });
          if (!res.ok) return [a.id, null];
          const data = await res.json();
          return [a.id, data.thumbnail_url || null];
        } catch {
          return [a.id, null];
        }
      }));
      if (!cancelled) setThumbMap(Object.fromEntries(results));
    };
    if (parentAssets.length > 0) loadThumbs();
    return () => { cancelled = true; };
  }, [parentAssets]);

  const triggerSetThumbnail = (assetId) => {
    thumbTargetRef.current = assetId;
    thumbInputRef.current?.click();
  };
  const handleThumbnailFileChange = async (e) => {
    const file = e.target.files?.[0];
    const assetId = thumbTargetRef.current;
    e.target.value = '';
    if (!file || !assetId) return;
    setThumbUploadingId(assetId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to set thumbnail');
      }
      const thumbRes = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, { credentials: 'include' });
      const thumbData = thumbRes.ok ? await thumbRes.json() : { thumbnail_url: null };
      setThumbMap((prev) => ({ ...prev, [assetId]: thumbData.thumbnail_url || null }));
      toast.success('Thumbnail updated');
    } catch (err) {
      toast.error(err.message || 'Failed to set thumbnail');
    } finally {
      setThumbUploadingId(null);
    }
  };

  const handleSaveChanges = () => {
    if (!nameDraft.trim() || !project) return;
    setSaving(true);
    const overrides = loadOverrides(project.creator_id);
    saveOverrides(project.creator_id, { ...overrides, [project.id]: { ...overrides[project.id], name: nameDraft.trim() } });
    setProject((p) => ({ ...p, name: nameDraft.trim() }));
    toast.success('Name change simulated locally (demo) — not saved to the server');
    setSaving(false);
  };

  const returnTo = `/app/create/workspace/project/${projectId}`;
  const editorBaseRoute = `/app/create/editor?projectId=${projectId}&returnTo=${encodeURIComponent(returnTo)}`;
  const clipsBaseRoute = `/app/create/clips?projectId=${projectId}&returnTo=${encodeURIComponent(returnTo)}`;

  // Highlights the clicked tile, then performs its action. Navigations
  // unmount the Hub immediately, so no extra state needs to persist beyond
  // the click itself.
  const selectTool = (key, fn) => { setActiveToolKey(key); fn(); };

  const actions = project ? [
    { key: 'upload', label: 'Upload', icon: Upload, onClick: () => selectTool('upload', () => navigate(`${returnTo}/upload`)) },
    { key: 'golive', label: 'Go Live', icon: Radio, onClick: () => selectTool('golive', () => navigate(`${returnTo}/live`)) },
    { key: 'editor', label: 'Editor', icon: Pencil, onClick: () => selectTool('editor', () => navigate(editorBaseRoute)) },
    { key: 'clips', label: 'Clips', icon: Scissors, onClick: () => selectTool('clips', () => navigate(clipsBaseRoute)) },
    { key: 'images_posts', label: 'Images & Posts', icon: Images, onClick: () => selectTool('images_posts', () => navigate(`${returnTo}/images-posts`)) },
    { key: 'optimize', label: 'Optimize', icon: Sparkles, onClick: () => selectTool('optimize', () => navigate(`/app/create/optimize?projectId=${projectId}&creatorId=${project.creator_id}`)) },
    { key: 'schedule', label: 'Schedule', icon: CalendarClock, onClick: () => selectTool('schedule', () => navigate(`/app/create/schedule?projectId=${projectId}&creatorId=${project.creator_id}`)) },
    { key: 'templates', label: 'Templates', icon: LayoutTemplate, onClick: () => selectTool('templates', () => navigate(`/app/create/templates?projectId=${projectId}&creatorId=${project.creator_id}&returnTo=${encodeURIComponent(returnTo)}`)) },
    { key: 'media', label: 'Media Library', icon: FolderOpen, onClick: () => selectTool('media', () => navigate(`/app/create/content-studio?projectId=${projectId}&returnTo=${encodeURIComponent(returnTo)}`)) },
  ] : [];

  // Arriving here from New Project's "pick a tool" flow — open that tool
  // automatically, exactly as if the user had clicked its tile here.
  useEffect(() => {
    if (autoToolFiredRef.current || !project || loading) return;
    const toolKey = searchParams.get('openTool');
    if (!toolKey) return;
    const match = actions.find(a => a.key === toolKey);
    if (match) { autoToolFiredRef.current = true; match.onClick(); }
  }, [project, loading, searchParams, actions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="project-overview-loading">
        <Loader2 className="h-6 w-6 text-[#8A93A6] animate-spin" />
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div className="container mx-auto p-6 max-w-7xl text-center py-24" data-testid="project-overview-not-found">
        <p className="text-[#8A93A6] mb-4">This project could not be found or you don't have access to it.</p>
        <Button variant="outline" className="border-white/10" onClick={() => navigate('/app/create/workspace')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Workspace
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Breadcrumb */}
      <div className="mb-4" data-testid="project-overview-breadcrumb">
        <p className="text-xs text-[#8A93A6] tracking-wide">
          <Link to="/app/create" className="hover:text-white transition-colors">Create</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <Link to="/app/create/workspace" className="hover:text-white transition-colors">Workspace</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <span className="text-[#C0C7D4]">{project.name}</span>
        </p>
      </div>

      {/* Page title */}
      <h1 className="text-3xl font-bold text-white mb-5" data-testid="production-hub-title">Production Hub</h1>

      {/* Project name and status — kept prominent */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
            <FolderOpen className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <p className="text-xl font-semibold text-white" data-testid="project-overview-name">{project.name}</p>
            <Badge variant="outline" className="border-white/10 text-[#8A93A6] text-xs mt-1 capitalize" data-testid="project-overview-status">
              {project.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Editable Project Name + Save Changes */}
      <div className="mb-8 max-w-md" data-testid="project-name-edit-section">
        <Label className="text-[#8A93A6]">Project Name</Label>
        <Input
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          className="bg-white/5 border-white/10 text-white mt-1"
          data-testid="project-form-name-input"
        />
        <Button onClick={handleSaveChanges} disabled={!nameDraft.trim() || saving} className="mt-2 bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" data-testid="project-form-submit-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Save Changes
        </Button>
      </div>

      <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbnailFileChange} />

      {/* Two-column: Production Tools (left) / Project Media (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="production-tools-panel">
          <ProjectFormDemo
            mode="existing"
            showNameField={false}
            actions={actions}
            activeToolKey={activeToolKey}
          />
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col" data-testid="project-media-panel">
          <h2 className="text-lg font-semibold text-white mb-4 shrink-0">Project Media</h2>
          {parentAssets.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-white/10 rounded-xl" data-testid="project-overview-empty-media">
              <FileVideo className="h-7 w-7 text-[#555E6E] mx-auto mb-2" />
              <p className="text-[#8A93A6] text-sm mb-1">No media attached to this project yet.</p>
              <p className="text-[#555E6E] text-xs mb-3">Upload a new file or attach something you've already uploaded elsewhere.</p>
              <div className="flex items-center justify-center gap-2">
                <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80 h-8 text-xs" onClick={() => selectTool('upload', () => navigate(`${returnTo}/upload`))} data-testid="project-media-empty-upload-btn">
                  <Upload className="h-3.5 w-3.5 mr-1.5" />Upload
                </Button>
                <Button size="sm" variant="outline" className="border-white/10 h-8 text-xs" onClick={() => navigate(`/app/create/content-studio?projectId=${projectId}&mode=attach&returnTo=${encodeURIComponent(returnTo)}`)} data-testid="project-media-empty-attach-btn">
                  <Link2 className="h-3.5 w-3.5 mr-1.5" />Attach Existing Media
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[420px] pr-1 -mr-1" data-testid="project-media-scroll-area">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="project-media-grid">
                {parentAssets.map((asset) => {
                  const isImage = isImageAsset(asset);
                  const previewUrl = thumbMap[asset.id] || (isImage ? asset.input_file_url : null);
                  return (
                    <Card key={asset.id} className="glass border-white/5" data-testid={`project-media-tile-${asset.id}`}>
                      <CardContent className="p-2.5 space-y-2">
                        <div className="w-full aspect-video rounded-md overflow-hidden bg-black/30 flex items-center justify-center" data-testid={`project-media-thumb-${asset.id}`}>
                          {previewUrl ? (
                            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
                          ) : isImage ? (
                            <ImageIcon className="h-5 w-5 text-[#555E6E]" />
                          ) : (
                            <FileVideo className="h-5 w-5 text-[#555E6E]" />
                          )}
                        </div>
                        <div className="flex items-start justify-between gap-1">
                          <div className="min-w-0">
                            <p className="text-xs text-white font-medium truncate" title={asset.title || asset.input_file_url}>
                              {asset.title || asset.input_file_url?.split('/').pop() || 'Untitled asset'}
                            </p>
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              <Badge variant="outline" className="border-white/10 text-[#8A93A6] text-[9px] px-1 py-0">{isImage ? 'Image' : 'Video'}</Badge>
                              <Badge variant="outline" className="border-white/10 text-[#8A93A6] text-[9px] px-1 py-0 capitalize">{asset.status}</Badge>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8A93A6] shrink-0" data-testid={`project-asset-menu-${asset.id}`}>
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#14171F] border-white/10">
                              {isImage ? (
                                <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed" title="The Editor only supports video right now" data-testid={`project-asset-action-edit-${asset.id}`}>
                                  <Pencil className="h-3.5 w-3.5 mr-2" />
                                  Edit (video only)
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => navigate(`/app/create/editor/${asset.id}`)} data-testid={`project-asset-action-edit-${asset.id}`}>
                                  <Pencil className="h-3.5 w-3.5 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem disabled={thumbUploadingId === asset.id} onClick={() => triggerSetThumbnail(asset.id)} data-testid={`project-asset-action-thumbnail-${asset.id}`}>
                                {thumbUploadingId === asset.id ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5 mr-2" />}
                                {thumbMap[asset.id] ? 'Change Thumbnail' : 'Set Thumbnail'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/app/create/optimize?assetId=${asset.id}&projectId=${project.id}&creatorId=${project.creator_id}&title=${encodeURIComponent(asset.title || '')}`)}
                                data-testid={`project-asset-action-schedule-${asset.id}`}
                              >
                                <CalendarClock className="h-3.5 w-3.5 mr-2" />
                                Optimize &amp; Schedule
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`${returnTo}/live`)} data-testid={`project-asset-action-golive-${asset.id}`}>
                                <Radio className="h-3.5 w-3.5 mr-2" />
                                Go Live
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Production Queue — existing projects only, full width below */}
      <div className="mt-8">
        <ProductionQueueDemo parentAssets={parentAssets} thumbMap={thumbMap} />
      </div>

      <div className="mt-8">
        <LinkedTemplateFoldersPanel creatorId={project.creator_id} projectId={projectId} />
      </div>
    </div>
  );
}
